/**
 * Talking to Supabase from the browser.
 *
 * The new API keys are not JWTs. A publishable or secret key goes on the
 * `apikey` header and only there: sent as `Authorization: Bearer …` the
 * platform tries to parse it as a JWT and rejects the request. A user's
 * session token is a JWT and does belong on `Authorization`, so the two are
 * kept apart here rather than merged the way a generic client would.
 *
 * DECISIONS_LOG.md, 4 October, item 7; build/tasks/COMMON.md.
 */

export const PUBLISHABLE_KEY_PREFIX = 'sb_publishable_'
export const SECRET_KEY_PREFIX = 'sb_secret_'

export class ApiKeyOnAuthorizationError extends Error {
  constructor() {
    super(
      'A Supabase API key must not be sent on Authorization: Bearer. ' +
        'Put it on the apikey header; Authorization carries a user session JWT only.',
    )
    this.name = 'ApiKeyOnAuthorizationError'
  }
}

export class SecretKeyInBrowserError extends Error {
  constructor() {
    super('A secret API key must never be sent from a browser.')
    this.name = 'SecretKeyInBrowserError'
  }
}

function isApiKey(value: string): boolean {
  // The header value may or may not carry the `Bearer ` prefix; both forms are
  // a key in the wrong place.
  const token = value.replace(/^Bearer\s+/i, '')
  return token.startsWith(PUBLISHABLE_KEY_PREFIX) || token.startsWith(SECRET_KEY_PREFIX)
}

/**
 * Builds the headers for one call. `accessToken` is a user session JWT, not an
 * API key; passing a key there throws rather than producing a request the
 * platform would reject with a misleading 401.
 */
export function supabaseHeaders(options: {
  apiKey: string
  accessToken?: string
  extra?: Record<string, string>
}): Headers {
  const { apiKey, accessToken, extra } = options

  if (apiKey.startsWith(SECRET_KEY_PREFIX)) throw new SecretKeyInBrowserError()

  const headers = new Headers(extra)
  for (const [name] of [...headers.entries()]) {
    if (name.toLowerCase() === 'authorization' && isApiKey(headers.get(name) ?? '')) {
      throw new ApiKeyOnAuthorizationError()
    }
  }

  headers.set('apikey', apiKey)
  if (accessToken !== undefined) {
    if (isApiKey(accessToken)) throw new ApiKeyOnAuthorizationError()
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  return headers
}

export interface SupabaseCallOptions extends Omit<RequestInit, 'headers'> {
  apiKey: string
  accessToken?: string
  headers?: Record<string, string>
}

/** `fetch` against the project URL with the key on `apikey`. */
export function supabaseFetch(
  baseUrl: string,
  path: string,
  options: SupabaseCallOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const { apiKey, accessToken, headers, ...init } = options
  return fetchImpl(new URL(path, baseUrl).toString(), {
    ...init,
    headers: supabaseHeaders({ apiKey, accessToken, extra: headers }),
  })
}
